// src/contexts/FeatureContext.jsx

import { createContext, useContext, useState, useEffect } from 'react';
import { useUser } from './UserContext';
import { apiService } from '@/services/api';
import { isOfflineModeEnabled, reportBootFailure } from '@/services/offline';

const PREFERRED_MODE_KEY = 'prism-preferred-mode';

// Solution ID mappings
const SOLUTION_IDS = {
  ERX_MOBILE: 4,
  REHAB_PRESCRIBED: 2,
  READY2EXERCISE: 3,
  FORM_BUILDER: 16,
  CORE_EHR: 19,
  PRISM_SCRIBE: 20,
  HOME_HEALTH: 24
};

const FeatureContext = createContext();

// Solutions that count toward mode detection. Home Health is an add-on to
// Core EHR and never changes an organization's mode on its own.
const MODE_EXCLUDED_SOLUTIONS = new Set(['home_health']);
export const countModeSolutions = (solutions) =>
  Object.entries(solutions || {}).filter(([key, value]) => !MODE_EXCLUDED_SOLUTIONS.has(key) && Boolean(value)).length;

export function FeatureProvider({ children }) {
  const { user, isAuthenticated } = useUser();
  const [solutions, setSolutions] = useState({
    erx_mobile: false,
    rehab_prescribed: false,
    ready2exercise: false,
    form_builder: false,
    core_ehr: false,
    prism_scribe: false,
    home_health: false,
  });
  const [solutionDetails, setSolutionDetails] = useState({
    erx_mobile: null,
    rehab_prescribed: null,
    ready2exercise: null,
    form_builder: null,
    core_ehr: null,
    prism_scribe: null,
    home_health: null
  });
  const [loading, setLoading] = useState(true);
  const [solutionsLoaded, setSolutionsLoaded] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSolutions = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }
      if (!user?.id || !user?.organization?.id) {
        // User is authenticated but profile hasn't loaded yet — stay in loading state
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const allSolutions = await apiService.getUserSolutions(user.organization.id, user.id);
        const rows = Array.isArray(allSolutions) ? allSolutions : [];

        const bySolutionId = {};
        for (const row of rows) {
          if (row && row.solution_id != null) {
            bySolutionId[row.solution_id] = row;
          }
        }

        const enabledSolutions = {
          erx_mobile: false,
          rehab_prescribed: false,
          ready2exercise: false,
          form_builder: false,
          core_ehr: false,
          scheduling: false,
          billing: false,
          telehealth: false,
          messaging: false,
          lab_orders: false,
          orders: false,
          fax: false,
          prism_scribe: false,
          home_health: false
        };

        const newSolutionDetails = {
          erx_mobile: null,
          rehab_prescribed: null,
          ready2exercise: null,
          form_builder: null,
          core_ehr: null,
          prism_scribe: null,
          home_health: null
        };

        const map = [
          [SOLUTION_IDS.ERX_MOBILE, 'erx_mobile'],
          [SOLUTION_IDS.REHAB_PRESCRIBED, 'rehab_prescribed'],
          [SOLUTION_IDS.READY2EXERCISE, 'ready2exercise'],
          [SOLUTION_IDS.FORM_BUILDER, 'form_builder'],
          [SOLUTION_IDS.CORE_EHR, 'core_ehr'],
          [SOLUTION_IDS.PRISM_SCRIBE, 'prism_scribe'],
          [SOLUTION_IDS.HOME_HEALTH, 'home_health'],
        ];

        for (const [id, key] of map) {
          const row = bySolutionId[id];
          if (row) {
            enabledSolutions[key] = true;
            newSolutionDetails[key] = row;

            if (key === 'core_ehr' && row.settings) {
              enabledSolutions.scheduling = row.settings.scheduling || false;
              enabledSolutions.billing = row.settings.billing || false;
              enabledSolutions.telehealth = row.settings.telehealth || false;
              enabledSolutions.messaging = row.settings.messaging || false;
              enabledSolutions.lab_orders = row.settings.lab_orders || false;
              enabledSolutions.orders = row.settings.orders || false;
              enabledSolutions.fax = row.settings.fax || false;
            }
          }
        }

        setSolutions(enabledSolutions);
        setSolutionDetails(newSolutionDetails);
        setSolutionsLoaded(true);
      } catch (err) {
        console.error('Error fetching solutions:', err);
        setError(err.message || 'Failed to fetch solutions');

        // Offline mode: a failed solutions fetch with no cached snapshot would
        // otherwise dead-end on the dashboard spinner (solutionsLoaded never
        // set). Surface the reconnect wall instead. No-op when the flag is off.
        if (isOfflineModeEnabled()) {
          reportBootFailure('solutions', err);
        }

        // In development, default to eRx Mobile
        if (process.env.NODE_ENV === 'development') {
          setSolutions(prev => ({ ...prev, erx_mobile: true }));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSolutions();
  }, [user?.id, user?.organization?.id, isAuthenticated]);

  // Bounded wait for the profile/organization. Authenticated with no
  // user.organization.id is normally a brief window while UserContext loads;
  // if it never resolves, stop spinning and surface 'no_organization' so the
  // home route can render an error card. Cleared as soon as the org arrives
  // (fetchSolutions then resets loading/error itself).
  useEffect(() => {
    if (!isAuthenticated) return;
    if (user?.id && user?.organization?.id) return;
    const NO_ORG_WAIT_MS = 10000;
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setError('no_organization');
    }, NO_ORG_WAIT_MS);
    return () => clearTimeout(timeoutId);
  }, [user?.id, user?.organization?.id, isAuthenticated]);

  // Helper functions
  const isSolutionEnabled = (solutionName) => solutions[solutionName] === true;

  const hasAnySolution = (...solutionNames) =>
    solutionNames.some(name => solutions[name] === true);

  const hasAllSolutions = (...solutionNames) =>
    solutionNames.every(name => solutions[name] === true);

  // Detect mode based on enabled solutions
  const getMode = () => {
    if (!solutionsLoaded) return 'loading';

    const enabledCount = countModeSolutions(solutions);

    // PrismScribe standalone mode - only when it's the ONLY solution
    if (solutions.prism_scribe && enabledCount === 1) {
      return 'prism_scribe';
    }

    // User preference override: scribe users with other solutions can opt into scribe view
    if (solutions.prism_scribe && enabledCount > 1 &&
        localStorage.getItem(PREFERRED_MODE_KEY) === 'prism_scribe') {
      return 'prism_scribe';
    }

    // Core EHR mode - this is the only case where we show the full EHR dashboard
    if (solutions.core_ehr) {
      // If core_ehr is enabled with other solutions, it's multi_feature
      // If core_ehr is the only solution, it's core_ehr mode
      return enabledCount > 1 ? 'multi_feature' : 'core_ehr';
    }

    // NO core_ehr below this point - users should see their primary solution UI
    
    // If only one solution is enabled, return that specific mode
    if (enabledCount === 1) {
      if (solutions.erx_mobile) return 'erx_mobile';
      if (solutions.form_builder) return 'form_builder';
      if (solutions.ready2exercise) return 'ready2exercise';
      if (solutions.rehab_prescribed) return 'rehab_prescribed';
    }

    // Multiple solutions but NO core_ehr
    // erx_mobile is typically the primary workflow, so prioritize it
    if (solutions.erx_mobile) return 'erx_mobile';
    if (solutions.form_builder) return 'form_builder';
    if (solutions.rehab_prescribed) return 'rehab_prescribed';
    if (solutions.ready2exercise) return 'ready2exercise';

    // Default fallback
    return 'erx_mobile';
};

  const getRehabMode = () => {
    const rehabSolution = solutionDetails.rehab_prescribed;

    // If no solution or settings, assume HIPAA for existing EHR users
    if (!rehabSolution?.settings?.mode) {
      return 'hipaa_active';
    }

    return rehabSolution.settings.mode;
  };

  const currentMode = getMode();

  const enabledCount = solutionsLoaded
    ? countModeSolutions(solutions)
    : 0;
  const canToggleScribeMode = solutionsLoaded && solutions.prism_scribe && enabledCount > 1;

  const toggleScribeMode = () => {
    if (currentMode === 'prism_scribe') {
      localStorage.removeItem(PREFERRED_MODE_KEY);
      window.location.href = '/';
    } else {
      localStorage.setItem(PREFERRED_MODE_KEY, 'prism_scribe');
      window.location.reload();
    }
  };

  const value = {
    solutions,
    solutionDetails,
    loading,
    solutionsLoaded,
    error,
    isSolutionEnabled,
    hasAnySolution,
    hasAllSolutions,
    mode: currentMode,
    canToggleScribeMode,
    toggleScribeMode,
    scribeDeploymentType: solutionDetails.prism_scribe?.deployment_type || null,
    getRehabMode,
    // Map solutions to features for backward compatibility
    features: {
      core_ehr: solutions.core_ehr,
      erx_mobile: solutions.erx_mobile,
      form_builder: solutions.form_builder,
      rehab_prescribed: solutions.rehab_prescribed,
      ready2exercise: solutions.ready2exercise,
      // These might be part of core EHR or separate solutions later
      scheduling: solutions.scheduling,
      billing: solutions.billing,
      telehealth: solutions.telehealth,
      messaging: solutions.messaging,
      orders: solutions.orders,
      fax: solutions.fax,
      prism_scribe: solutions.prism_scribe,
      home_health: solutions.home_health
    },
    refetchSolutions: () => {
      setLoading(true);
      setError(null);
      // Trigger useEffect by changing a dependency
      setSolutions({ ...solutions });
    }
  };

  return (
    <FeatureContext.Provider value={value}>
      {children}
    </FeatureContext.Provider>
  );
}

export function useFeatureFlags() {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error('useFeatureFlags must be used within a FeatureProvider');
  }
  return context;
}